<template>
  <section id="reviews" class="reviews">
    <div class="wrap">
      <div class="head-row">
        <div class="head-left">
          <p class="kicker mono">Reviews</p>
          <h2 class="display" ref="head">
            <span class="ln"><span>Trusted by the neighbourhood.</span></span>
          </h2>
        </div>

        <div class="head-right">
          <!-- overall Google rating badge -->
          <a class="score" :href="profileUrl || '#'" target="_blank" rel="noopener"
             :class="{ live: profileUrl }">
            <span class="score-num display">{{ rating ? rating.toFixed(1) : '4.9' }}</span>
            <span class="score-meta">
              <span class="score-stars" aria-hidden="true">★★★★★</span>
              <span class="score-count mono">{{ total ? total + ' Google reviews' : 'Google reviews' }}</span>
            </span>
          </a>
          <div class="arrows" v-if="shown.length > 3">
            <button @click="scrollBy(-1)" aria-label="Previous">←</button>
            <button @click="scrollBy(1)" aria-label="Next">→</button>
          </div>
        </div>
      </div>

      <div class="rail" ref="grid">
        <figure v-for="(r, i) in shown" :key="r.author + i" class="card">
          <div class="card-top">
            <div class="who">
              <span class="avatar" :style="r.photo ? { backgroundImage: `url(${r.photo})` } : {}">
                <template v-if="!r.photo">{{ initial(r.author) }}</template>
              </span>
              <div class="who-txt">
                <span class="name">{{ r.author }}</span>
                <span class="when mono">{{ r.relative || 'Verified customer' }}</span>
              </div>
            </div>
            <span class="stars" aria-hidden="true">★★★★★</span>
          </div>
          <blockquote>{{ r.text }}</blockquote>
          <figcaption class="mono">
            <svg class="g" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            Posted on Google
          </figcaption>
        </figure>
      </div>

      <p class="attrib mono" v-if="profileUrl">
        Live from <a :href="profileUrl" target="_blank" rel="noopener">our Google Business profile</a>
      </p>
    </div>
  </section>
</template>

<script>
import { maskReveal, gsap } from '@/composables/useReveal'

// fallback shown only if the live API is unreachable (dev / misconfig)
const FALLBACK = [
  { author: 'Priya R.', text: 'Fixed my laptop screen same day. Fair price and genuinely helpful.', relative: 'Verified customer', rating: 5, photo: null },
  { author: 'Arjun M.', text: 'They recovered all my data after a hard drive failure. Lifesavers.', relative: 'Verified customer', rating: 5, photo: null },
  { author: 'Kavya S.', text: 'Upgraded my old desktop with an SSD — feels brand new. Highly recommend.', relative: 'Verified customer', rating: 5, photo: null }
]

export default {
  name: 'ReviewsSection',
  data() {
    return {
      reviews: [],
      rating: null,
      total: null,
      profileUrl: null,
      loaded: false
    }
  },
  computed: {
    // top-rated only, capped for a clean grid
    shown() {
      const list = this.reviews.length ? this.reviews : FALLBACK
      return list.filter((r) => r.rating >= 5).slice(0, 10)
    }
  },
  async mounted() {
    maskReveal(this.$refs.head.querySelectorAll('.ln span'), { trigger: this.$el, start: 'top 80%' })
    await this.fetchReviews()
    this.$nextTick(() => { this.animateCards(); this.enableDrag(); this.startAuto() })
  },
  beforeUnmount() {
    this._dragCleanup && this._dragCleanup()
    clearInterval(this._auto)
    const ld = document.getElementById('reviews-schema')
    if (ld) ld.remove()
  },
  methods: {
    initial(name) { return (name || '?').trim().charAt(0).toUpperCase() },
    scrollBy(dir) {
      const rail = this.$refs.grid
      rail.scrollBy({ left: dir * rail.clientWidth * 0.6, behavior: 'smooth' })
    },
    tick() {
      const rail = this.$refs.grid
      if (!rail) return
      const card = rail.querySelector('.card')
      const step = card ? card.offsetWidth + 24 : rail.clientWidth * 0.4
      if (rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 8) {
        rail.scrollTo({ left: 0, behavior: 'smooth' })
      } else {
        rail.scrollBy({ left: step, behavior: 'smooth' })
      }
    },
    startAuto() {
      const rail = this.$refs.grid
      if (!rail || this.shown.length <= 3) return
      clearInterval(this._auto)
      this._auto = setInterval(this.tick, 5000)
      rail.addEventListener('pointerenter', () => clearInterval(this._auto))
      rail.addEventListener('pointerleave', () => { clearInterval(this._auto); this._auto = setInterval(this.tick, 5000) })
    },
    enableDrag() {
      const rail = this.$refs.grid
      if (!rail) return
      let down = false, startX = 0, startScroll = 0, moved = false
      const start = (e) => { down = true; moved = false; startX = e.pageX; startScroll = rail.scrollLeft; rail.classList.add('grabbing') }
      const move = (e) => { if (!down) return; const dx = e.pageX - startX; if (Math.abs(dx) > 4) moved = true; rail.scrollLeft = startScroll - dx }
      const end = () => { down = false; rail.classList.remove('grabbing') }
      const click = (e) => { if (moved) { e.preventDefault(); e.stopPropagation() } }
      rail.addEventListener('pointerdown', start)
      window.addEventListener('pointermove', move)
      window.addEventListener('pointerup', end)
      rail.addEventListener('click', click, true)
      this._dragCleanup = () => {
        rail.removeEventListener('pointerdown', start)
        window.removeEventListener('pointermove', move)
        window.removeEventListener('pointerup', end)
        rail.removeEventListener('click', click, true)
      }
    },
    async fetchReviews() {
      try {
        const res = await fetch('/api/reviews')
        if (!res.ok) throw new Error('bad status')
        const d = await res.json()
        if (Array.isArray(d.reviews) && d.reviews.length) {
          this.reviews = d.reviews
          this.rating = d.rating
          this.total = d.total
          this.profileUrl = d.profileUrl
          this.updateReviewSchema()
        }
      } catch (e) {
        // silently fall back to curated set
      } finally {
        this.loaded = true
      }
    },
    updateReviewSchema() {
      if (!this.rating || !this.total) return
      let ld = document.getElementById('reviews-schema')
      if (!ld) {
        ld = document.createElement('script')
        ld.type = 'application/ld+json'
        ld.id = 'reviews-schema'
        document.head.appendChild(ld)
      }
      ld.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'ComputerRepairService',
        '@id': 'https://nexxtgencomputers.in/#business',
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: this.rating,
          reviewCount: this.total,
          bestRating: 5,
          worstRating: 1
        }
      })
    },
    animateCards() {
      gsap.from(this.$refs.grid.querySelectorAll('.card'), {
        y: 30, opacity: 0, duration: 0.9, ease: 'power4.out', stagger: 0.12,
        clearProps: 'transform,opacity',
        scrollTrigger: { trigger: this.$refs.grid, start: 'top 90%' }
      })
    }
  }
}
</script>

<style scoped>
.reviews { background: var(--grey); color: var(--ink);
  padding: clamp(44px, 5vw, 80px) 0 clamp(44px, 5vw, 80px); }
.wrap { width: min(1360px, 94vw); margin: 0 auto; }

.head-row { display: flex; align-items: flex-end; justify-content: space-between;
  gap: 24px; margin-bottom: clamp(16px, 2vw, 28px); flex-wrap: wrap; }
.kicker { color: var(--coral); font-size: 12px; letter-spacing: 3px; text-transform: uppercase; margin: 0 0 18px; }
.display { font-family: var(--font-display); font-weight: 700; letter-spacing: -2px; line-height: 1.05;
  font-size: clamp(34px, 5vw, 72px); margin: 0; }
.ln { display: block; overflow: hidden; }

/* overall score badge */
.score { display: inline-flex; align-items: center; gap: 16px; text-decoration: none; color: var(--ink);
  padding: 14px 20px; border: 1px solid rgba(0,0,0,0.14); border-radius: 14px;
  background: rgba(0,0,0,0.03); transition: transform 0.3s var(--ease), border-color 0.3s, box-shadow 0.3s; }
.score.live:hover { transform: translateY(-2px); border-color: var(--coral);
  box-shadow: 0 12px 30px rgba(0,0,0,0.08); }
.score-num { font-size: clamp(30px, 3vw, 44px); font-weight: 700; letter-spacing: -1px; line-height: 1; }
.score-meta { display: flex; flex-direction: column; gap: 4px; }
.score-stars { color: var(--coral); letter-spacing: 2px; font-size: 14px; }
.score-count { font-size: 11px; letter-spacing: 1px; color: #5a5a60; }

.head-right { display: flex; align-items: center; gap: 20px; }
.arrows { display: flex; gap: 10px; }
.arrows button { width: 48px; height: 48px; border-radius: 50%; border: none; cursor: pointer;
  background: var(--ink); color: #fff; font-size: 18px; transition: background 0.25s, transform 0.2s; }
.arrows button:hover { background: var(--coral); color: var(--ink); transform: scale(1.06); }

.rail { display: flex; align-items: stretch; gap: clamp(16px, 2vw, 24px);
  overflow-x: auto; overflow-y: visible; scroll-snap-type: x mandatory;
  scrollbar-width: none; cursor: grab; user-select: none; padding: 16px 4px 28px; }
.rail.grabbing { cursor: grabbing; scroll-snap-type: none; }
.rail::-webkit-scrollbar { display: none; }
.card { position: relative; flex: 0 0 clamp(300px, 32%, 420px); scroll-snap-align: start;
  margin: 0; padding: clamp(28px, 2.8vw, 40px); overflow: hidden;
  background: linear-gradient(180deg, #d4d4d4, #c4c4c4);
  border: 1px solid rgba(0,0,0,0.10); border-radius: 22px;
  display: flex; flex-direction: column; gap: 20px;
  box-shadow: 0 1px 1px rgba(255,255,255,0.5) inset, 0 6px 18px rgba(0,0,0,0.04);
  transition: transform 0.45s var(--ease), border-color 0.45s, box-shadow 0.45s; }
.card::before { content: '\201C'; position: absolute; top: 6px; right: 22px;
  font-family: var(--font-display); font-weight: 700; font-size: 110px; line-height: 1;
  color: rgba(242,107,78,0.14); pointer-events: none; }
.card:hover { transform: translateY(-6px); border-color: rgba(242,107,78,0.55);
  box-shadow: 0 1px 1px rgba(255,255,255,0.5) inset,
    0 10px 24px rgba(0,0,0,0.06), 0 4px 16px rgba(242,107,78,0.12); }

.card-top { display: flex; align-items: center; justify-content: space-between; gap: 12px; position: relative; z-index: 1; }
.who { display: flex; align-items: center; gap: 13px; }
.avatar { width: 46px; height: 46px; border-radius: 50%; flex: none;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, var(--coral), var(--coral-deep));
  background-size: cover; background-position: center;
  color: #fff; font-family: var(--font-display); font-weight: 700; font-size: 18px;
  box-shadow: 0 0 0 3px rgba(242,107,78,0.18); }
.who-txt { display: flex; flex-direction: column; gap: 3px; }
.name { font-family: var(--font-display); font-weight: 600; font-size: 16px; letter-spacing: -0.3px; }
.when { font-size: 11px; letter-spacing: 0.5px; color: #6a6a70; }
.stars { color: var(--coral); letter-spacing: 2px; font-size: 15px; }

blockquote { margin: 0; font-size: clamp(15px, 1.45vw, 18px); line-height: 1.62; flex: 1;
  color: #1a1a20; position: relative; z-index: 1; }
figcaption { display: flex; align-items: center; gap: 8px; font-size: 11px; letter-spacing: 1px;
  text-transform: uppercase; color: #6a6a70; padding-top: 16px;
  border-top: 1px solid rgba(0,0,0,0.1); position: relative; z-index: 1; }
figcaption .g { flex: none; }

.attrib { margin: clamp(28px, 3vw, 40px) 0 0; font-size: 12px; letter-spacing: 1px; color: #5a5a60; }
.attrib a { color: var(--ink); text-underline-offset: 3px; }
.attrib a:hover { color: var(--coral); }

@media (max-width: 900px) { .card { flex-basis: 84%; } .head-row { align-items: flex-start; } }
</style>
